import { useMemo } from "react";
import {
  COMPANY_COLUMN_ALIASES,
  buildGenericCompanyColumn,
} from "@/lib/companyColumnRenderers";
import { useNavigate } from "react-router-dom";
import {
  Archive,
  Eye,
  Mail,
  MoreHorizontal,
  PhoneCall,
} from "lucide-react";
import {
  type ColumnDef,
  type ColumnSizingState,
  type RowSelectionState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { TagBadge } from "@/components/tags/TagBadge";
import { cn } from "@/lib/utils";
import {
  formatDate,
  formatRelativeDate,
  initials,
} from "@/lib/formatters";
import {
  daysSince,
  formatRelationshipDuration,
  getServiceStyle,
} from "@/lib/clients";
import type {
  AvailableColumn,
  Company,
  Contact,
  ProjectMember,
  User,
} from "@/api/types";
import { HiddenDetail, isHidden } from "@/components/shared/HiddenDetail";

interface ClientsTableProps {
  items: Company[];
  contactsByCompanyId: Record<number, Contact | null>;
  contactsLoading: boolean;
  members: ProjectMember[];
  projectSlug: string;
  showTags?: boolean;
  /** When false, the row checkbox column is hidden (no bulk perms). */
  selectable?: boolean;
  selectedIds: Set<number>;
  onSelectionChange: (ids: Set<number>) => void;
  /** Per-row dropdown — Archive (when present). */
  onAskArchive?: (company: Company) => void;
  /** Per-row dropdown — opens the call-log sheet (when present). */
  onAskLogInteraction?: (company: Company) => void;
  /** When provided, the existing column set is filtered/reordered to
   *  match. Catalog keys without a dedicated rich renderer fall through
   *  to the shared generic ColumnDef factory. */
  visibleColumnKeys?: string[];
  availableColumns?: AvailableColumn[];
  currentUser?: User | null;
  /** Per-column widths, keyed by column id. The page owns this state
   *  so it can persist to localStorage / saved views. */
  columnSizing?: ColumnSizingState;
  onColumnSizingChange?: (next: ColumnSizingState) => void;
}

export function ClientsTable({
  items,
  contactsByCompanyId,
  contactsLoading,
  members,
  projectSlug,
  showTags = false,
  selectable = true,
  selectedIds,
  onSelectionChange,
  onAskArchive,
  onAskLogInteraction,
  visibleColumnKeys,
  availableColumns,
  currentUser = null,
  columnSizing,
  onColumnSizingChange,
}: ClientsTableProps) {
  const navigate = useNavigate();

  const rowSelection = useMemo<RowSelectionState>(() => {
    const out: RowSelectionState = {};
    for (const id of selectedIds) out[String(id)] = true;
    return out;
  }, [selectedIds]);

  const columns = useMemo<ColumnDef<Company>[]>(() => {
    const cols: ColumnDef<Company>[] = [];
    if (selectable) {
      cols.push({
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllRowsSelected()
                ? true
                : table.getIsSomeRowsSelected()
                  ? "indeterminate"
                  : false
            }
            onCheckedChange={(value) =>
              table.toggleAllRowsSelected(value === true)
            }
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(value === true)}
            onClick={(e) => e.stopPropagation()}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        enableResizing: false,
        size: 36,
      });
    }
    cols.push(
      {
        id: "company",
        accessorKey: "name",
        header: "Company",
        // Default width — user can drag to fit their data.
        size: 200,
        minSize: 60,
        maxSize: 400,
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-slate-100 text-xs font-bold text-slate-600">
                {c.name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="line-clamp-2 break-words font-medium text-slate-900">
                  {c.name}
                </div>
                {c.domain && (
                  <div className="truncate text-xs text-slate-400">
                    {c.domain}
                  </div>
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: "contact",
        header: "Contact",
        size: 200,
        minSize: 60,
        maxSize: 320,
        cell: ({ row }) => {
          const contact = contactsByCompanyId[row.original.id];
          if (contactsLoading && contact === undefined) {
            return (
              <span className="inline-block h-3 w-20 animate-pulse rounded bg-slate-100" />
            );
          }
          if (!contact) {
            return <span className="text-xs text-slate-400">—</span>;
          }
          return (
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-slate-900">
                {contact.full_name}
              </div>
              {contact.email && (
                <a
                  href={`mailto:${contact.email}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 truncate text-xs text-indigo-600 hover:text-indigo-500"
                >
                  <Mail className="h-3 w-3 shrink-0" />
                  <span className="truncate">{contact.email}</span>
                </a>
              )}
              {isHidden(contact, "email") && <HiddenDetail label="Email" className="text-xs" />}
            </div>
          );
        },
      },
      {
        id: "service",
        header: "Service",
        size: 140,
        minSize: 60,
        maxSize: 220,
        cell: ({ row }) => {
          const service = row.original.metadata.service;
          if (typeof service !== "string" || !service) {
            return <span className="text-xs text-slate-400">—</span>;
          }
          const styles = getServiceStyle(service);
          return (
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
                styles.bg,
                styles.text,
                styles.border,
              )}
            >
              <span className={cn("h-1.5 w-1.5 rounded-full", styles.dot)} />
              {service}
            </span>
          );
        },
      },
      {
        id: "client_since",
        header: "Client since",
        size: 130,
        minSize: 60,
        maxSize: 200,
        cell: ({ row }) => {
          const c = row.original;
          const signed =
            typeof c.metadata.signed_date === "string"
              ? c.metadata.signed_date
              : null;
          const sinceDate = signed ?? c.pipeline_status_changed_at;
          if (!sinceDate) {
            return <span className="text-xs text-slate-400">—</span>;
          }
          const month = formatDate(sinceDate, "MMM yyyy");
          const duration = formatRelationshipDuration(sinceDate);
          return (
            <div>
              <div className="text-sm text-slate-700">{month}</div>
              {duration && (
                <div className="text-xs text-slate-400">{duration}</div>
              )}
            </div>
          );
        },
      },
      {
        id: "owner",
        accessorKey: "owner_id",
        header: "Owner",
        size: 140,
        minSize: 60,
        maxSize: 220,
        cell: ({ getValue }) => {
          const ownerId = getValue<string | null>();
          if (!ownerId) {
            return <span className="text-xs text-slate-400">Unassigned</span>;
          }
          const member = members.find((m) => m.id === ownerId);
          if (!member) {
            return (
              <div className="flex items-center gap-2">
                <Avatar className="h-6 w-6 border border-slate-200">
                  <AvatarFallback className="bg-slate-300 text-[10px] font-semibold text-white">
                    ?
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm text-slate-400">Unknown</span>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6 border border-slate-200">
                <AvatarFallback className="bg-slate-900 text-[10px] font-semibold text-white">
                  {initials(member.full_name)}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm text-slate-600">
                {member.full_name}
              </span>
            </div>
          );
        },
      },
      {
        id: "source",
        accessorKey: "source",
        header: "Source",
        size: 110,
        minSize: 60,
        maxSize: 160,
        cell: ({ getValue }) => {
          const source = (getValue<string>() as string).toLowerCase();
          const isImport = source === "import";
          return (
            <span
              className={cn(
                "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
                isImport
                  ? "border-slate-200 bg-slate-100 text-slate-600"
                  : "border-indigo-200 bg-indigo-50 text-indigo-700",
              )}
            >
              {isImport ? "Imported" : "Pipeline"}
            </span>
          );
        },
      },
    );

    if (showTags) {
      cols.push({
        id: "tags",
        header: "Tags",
        size: 220,
        minSize: 60,
        maxSize: 360,
        cell: ({ row }) => {
          const tags = row.original.tags;
          if (!tags || tags.length === 0) {
            return <span className="text-xs text-slate-300">—</span>;
          }
          return (
            <div
              className="flex max-h-[44px] flex-wrap items-center gap-1 overflow-hidden"
              title={tags.map((t) => t.name).join(", ")}
            >
              {tags.map((t) => (
                <TagBadge key={t.id} tag={t} size="sm" />
              ))}
            </div>
          );
        },
      });
    }

    cols.push({
      id: "last_activity",
      header: "Last activity",
      size: 120,
      minSize: 60,
      maxSize: 180,
      cell: ({ row }) => {
        const updatedAt = row.original.updated_at;
        const days = daysSince(updatedAt);
        if (days > 30) {
          return (
            <span
              className="inline-flex items-center whitespace-nowrap rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700"
              title={formatDate(updatedAt)}
            >
              Inactive · {days} days
            </span>
          );
        }
        return (
          <span className="text-xs text-slate-500">
            {formatRelativeDate(updatedAt)}
          </span>
        );
      },
    });

    if (onAskArchive || onAskLogInteraction) {
      cols.push({
        id: "row_actions",
        header: "",
        size: 36,
        enableResizing: false,
        cell: ({ row }) => {
          const c = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger
                asChild
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="rounded-md p-1.5 text-slate-400 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-700 focus-visible:opacity-100 group-hover:opacity-100"
                  aria-label={`More actions for ${c.name}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-48"
                onClick={(e) => e.stopPropagation()}
              >
                <DropdownMenuItem
                  onSelect={() =>
                    navigate(`/projects/${projectSlug}/companies/${c.id}`)
                  }
                >
                  <Eye className="mr-2 h-3.5 w-3.5" />
                  View details
                </DropdownMenuItem>
                {onAskLogInteraction && (
                  <DropdownMenuItem
                    onSelect={() => onAskLogInteraction(c)}
                  >
                    <PhoneCall className="mr-2 h-3.5 w-3.5" />
                    Log interaction
                  </DropdownMenuItem>
                )}
                {onAskArchive && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-amber-700 focus:bg-amber-50 focus:text-amber-700"
                      onSelect={() => onAskArchive(c)}
                    >
                      <Archive className="mr-2 h-3.5 w-3.5" />
                      Archive
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      });
    }

    return cols;
  }, [
    selectable,
    contactsByCompanyId,
    contactsLoading,
    members,
    navigate,
    projectSlug,
    showTags,
    onAskArchive,
    onAskLogInteraction,
  ]);

  // Same filter+reorder pattern as CompanyTable. Select stays first,
  // last_activity / row_actions stay last (the trailing columns are
  // contextual to the Clients tab and shouldn't be hideable). Catalog
  // keys without a dedicated renderer fall back to the shared factory.
  const finalColumns = useMemo<ColumnDef<Company>[]>(() => {
    if (!visibleColumnKeys) return columns;
    const byKey = new Map<string, ColumnDef<Company>>();
    for (const c of columns) {
      const id = c.id;
      if (!id) continue;
      byKey.set(id, c);
      const aliases = COMPANY_COLUMN_ALIASES[id];
      if (aliases) for (const a of aliases) byKey.set(a, c);
    }
    const out: ColumnDef<Company>[] = [];
    const selectCol = columns.find((c) => c.id === "select");
    if (selectCol) out.push(selectCol);
    const seen = new Set<string>([
      "select",
      "last_activity",
      "row_actions",
    ]);
    for (const key of visibleColumnKeys) {
      if (seen.has(key)) continue;
      seen.add(key);
      const c = byKey.get(key);
      if (c) {
        out.push(c);
        continue;
      }
      const fallback = buildGenericCompanyColumn(
        key,
        availableColumns,
        members,
        currentUser,
      );
      if (fallback) out.push(fallback);
    }
    const lastActivity = columns.find((c) => c.id === "last_activity");
    if (lastActivity) out.push(lastActivity);
    const rowActions = columns.find((c) => c.id === "row_actions");
    if (rowActions) out.push(rowActions);
    return out;
  }, [
    columns,
    visibleColumnKeys,
    availableColumns,
    members,
    currentUser,
  ]);

  const table = useReactTable({
    data: items,
    columns: finalColumns,
    state: { rowSelection, ...(columnSizing ? { columnSizing } : {}) },
    enableRowSelection: selectable,
    columnResizeMode: "onChange",
    getRowId: (row) => String(row.id),
    onRowSelectionChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(rowSelection) : updater;
      const ids = new Set<number>();
      for (const [id, selected] of Object.entries(next)) {
        if (selected) ids.add(Number(id));
      }
      onSelectionChange(ids);
    },
    onColumnSizingChange: onColumnSizingChange
      ? (updater) => {
          const next =
            typeof updater === "function"
              ? updater(columnSizing ?? {})
              : updater;
          onColumnSizingChange(next);
        }
      : undefined,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="overflow-x-auto rounded-plane bg-white shadow-plane">
      <Table
        // See CompanyTable — minWidth, not width, so the table fills
        // the container by default and only forces scroll once the
        // operator drags the columns past it.
        style={{ minWidth: `${table.getCenterTotalSize()}px` }}
      >
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="border-slate-900/[0.06] hover:bg-transparent">
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  style={{ width: `${header.getSize()}px` }}
                  className="group relative text-[11px] font-medium uppercase tracking-wider text-slate-500"
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                  {header.column.getCanResize() && (
                    <div
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      onClick={(e) => e.stopPropagation()}
                      onDoubleClick={() => header.column.resetSize()}
                      role="separator"
                      aria-orientation="vertical"
                      aria-label="Resize column (double-click to reset)"
                      style={{ transform: "translateX(50%)" }}
                      className={cn(
                        "absolute right-0 top-0 z-10 h-full w-3 cursor-col-resize select-none touch-none",
                        "bg-transparent transition-colors",
                        "group-hover:bg-indigo-200",
                        header.column.getIsResizing() && "bg-indigo-500",
                      )}
                    />
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() ? "selected" : undefined}
              onClick={() =>
                navigate(
                  `/projects/${projectSlug}/companies/${row.original.id}`,
                )
              }
              className={cn(
                "group cursor-pointer border-slate-900/[0.06] transition-colors duration-150 motion-reduce:transition-none",
                row.getIsSelected()
                  ? "bg-indigo-500/[0.07] hover:bg-indigo-500/[0.1]"
                  : "hover:bg-indigo-500/[0.04]",
              )}
            >
              {row.getVisibleCells().map((cell) => {
                // The select column owns the entire cell's hit area —
                // see CompanyTable for the same fix.
                const isSelectCell = cell.column.id === "select";
                return (
                  <TableCell
                    key={cell.id}
                    style={{ width: `${cell.column.getSize()}px` }}
                    // line-clamp + max-h on the inner content handles
                    // overflow per column; no cell-level clip.
                    className={cn(
                      "py-3 align-top text-sm",
                      isSelectCell && "cursor-pointer",
                    )}
                    onClick={
                      isSelectCell
                        ? (e) => {
                            e.stopPropagation();
                            row.toggleSelected(!row.getIsSelected());
                          }
                        : undefined
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
